<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BillingFrequencySelector',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'update:modelValue']);

  const props = defineProps({
    ...userComponentProps,
    modelValue: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const internalValue = ref(null);
  const options = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    internalValue: internalValue.value,
    options: options.value,
    ux: ux.value,
  }));

  const $$load_internalValue = () => {
    return props.modelValue;
  };
  const $$load_options = () => {
    return $app.value.repo.get('ui_metadata', {
      path: 'billing_frequency_options',
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'internalValue' }, { name: 'options' }, { name: 'ux' }],
    loaders: { internalValue: $$load_internalValue, options: $$load_options },
    ux: {},
    dataRefs: { internalValue, options, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.modelValue,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modelValue');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_5962862420439144_emit_ckx9p2rzt_getActionArgs = async ($event) => {
    return {
      event: 'update:modelValue',
      value: internalValue.value,
    };
  };

  const act_5962862420439144_emit_ckx9p2rzt = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_ckx9p2rzt_getActionArgs,
  });
  const inst_evh_5962862420439144_input_lcc9qpeju =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_ckx9p2rzt,
      event: 'input',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_input_lcc9qpeju],
    event: 'input',
  });

  const actions = {
    inst_evh_5962862420439144_input_lcc9qpeju,
    inst_evh_5962862420439144_input,
  };

  const evh_5962862420439144_input_lcc9qpeju = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_input_lcc9qpeju.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_input = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div class="input-group" v-if="loaded">
    <div class="input-group-text">
      <ItpIcon icon="material-symbols:repeat-rounded"> </ItpIcon>
    </div>
    <ItpFormNativeSelect
      name="billing_frequency"
      v-model="internalValue"
      :options="options"
      valueField="value"
      textField="name"
      required
      :readonly="readonly"
      @input="evh_5962862420439144_input($event, {})"
    >
    </ItpFormNativeSelect>
  </div>
</template>
